/**
 * `false` on the commit that mounts a day host, `true` once that host's entrance has finished.
 *
 * It exists for one problem that both of `DayPanelHost`'s boxes have — the panel and the day modal
 * are two boxes around one form, and both pay the same cost. It moved out of the panel component
 * when the modal landed, and stayed out when the two components became one.
 *
 * ── The cost it exists to defer ─────────────────────────────────────────────────────────────
 * A host's box and its entrance must not wait for anything inside it, and the one thing inside a day
 * host that is neither on screen nor reachable when it opens is the closed drawer's catalogue. The
 * rail renders the whole library with no cap and no virtualization — 488 rows, and 5231 nodes in the
 * panel against the ~250 the day itself needs — and it did that in the SAME commit that mounts the
 * popup. The dock's press was therefore one 220–320ms task, and the entrance could not begin until
 * it ended, which is the delay the owner reported as "STRASZNIE długo się odpala ten boczny panel".
 *
 * Latched once and never reset: the drawer keeps whatever it has been given, and unmounting the rail
 * on every close would only cost an empty box sliding back out on the way home.
 *
 * ── Why the WHOLE entrance, and not the first idle after the commit ──────────────────────────
 * Measured, both ways, on the panel. The main thread is idle the instant the popup's own commit
 * ends, so an idle callback — even behind two frames — ran at 115ms, in the very frame the
 * transition was due to begin, and its 73ms of catalogue rendering pushed the panel's first movement
 * out to 189ms and then let it jump 378px in the next 30ms. Waiting two frames moved the callback by
 * three milliseconds and fixed nothing.
 *
 * So the wait is the transition's own end event, which is the condition rather than a proxy for it,
 * and needs no copy of a duration token in JavaScript. Under `prefers-reduced-motion` the app
 * collapses every transition to 0.001ms and the event lands on the next frame, which is the right
 * answer there too. The timer is the fallback for a renderer that fires no event at all — a host that
 * somehow opens without transitioning must still end up with a catalogue behind its drawer.
 *
 * Both hosts animate `transform` on their own root, which is what makes one hook serve them: the
 * panel slides in from the right edge, the modal rises and scales, and either way `transform` on the
 * element itself is the thing that ends when the host has arrived.
 */

import { useEffect, useState, type RefObject } from 'react';

/**
 * How long the catalogue may wait before it is mounted anyway. Longer than either host's entrance,
 * which is what it is standing in for.
 */
const SLIDE_TIMEOUT_MS = 600;

export function useMountAfterSlide(ref: RefObject<HTMLElement | null>): boolean {
  const [isMounted, setMounted] = useState(false);

  useEffect(() => {
    if (isMounted) return undefined;
    const host = ref.current;
    const arrive = () => {
      setMounted(true);
    };
    const timer = globalThis.setTimeout(arrive, SLIDE_TIMEOUT_MS);
    // The host's own transform and nothing bubbling up from inside it — the day form below is full
    // of transitions, and any one of them would answer for the entrance.
    const onEnd = (event: TransitionEvent) => {
      if (event.target === host && event.propertyName === 'transform') arrive();
    };
    host?.addEventListener('transitionend', onEnd);
    return () => {
      globalThis.clearTimeout(timer);
      host?.removeEventListener('transitionend', onEnd);
    };
  }, [isMounted, ref]);

  return isMounted;
}
